/**
 * CalendarEventList — renders a list of iCal event occurrences.
 *
 * Ported from v1 `UpcomingListCalendarList.tsx`.
 * Adapted to v2's Tailwind + Lucide design system.
 */

import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import dayjs from "dayjs"
import EyeIcon from "@/shared/icons/totodoicons/stroke/eye.svg?react"
import type { EventOccurrence } from "./types"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type CalendarListPeriodType = "day" | "week" | "month"

export interface CalendarListPeriod {
  id: string
  type: CalendarListPeriodType
  startDate: dayjs.Dayjs
  endDate: dayjs.Dayjs
}

interface CalendarEventListProps {
  events: EventOccurrence[]
  period: CalendarListPeriod
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CalendarEventList({ events, period }: CalendarEventListProps) {
  const { t } = useTranslation()
  const [visibleCount, setVisibleCount] = useState(10)

  const sortedEvents = useMemo(
    () =>
      [...events].sort((a, b) => {
        const dateA = a.occurrenceDate
        const dateB = b.occurrenceDate
        const allDayA = a.allDay ? 1 : 0
        const allDayB = b.allDay ? 1 : 0
        const recurringA = a.rrule ? 1 : 0
        const recurringB = b.rrule ? 1 : 0
        const timeA = a.occurrenceStart
        const timeB = b.occurrenceStart

        return dateA === dateB
          ? allDayB - allDayA || recurringB - recurringA || timeA - timeB
          : dateA < dateB
            ? -1
            : 1
      }),
    [events],
  )

  const visibleEvents = sortedEvents.slice(0, visibleCount)
  const hasMore = sortedEvents.length > visibleCount

  if (events.length === 0) return null

  return (
    <div className="w-full">
      {visibleEvents.map((event, i) => (
        <CalendarEventRow
          key={`${event.uid}-${event.occurrenceDate}-${i}`}
          event={event}
          period={period}
        />
      ))}

      {hasMore && (
        <button
          type="button"
          onClick={() => setVisibleCount((prev) => prev + 10)}
          className="flex items-center gap-1.5 text-sm font-semibold text-primary mt-2.5"
        >
          <EyeIcon className="size-5" />
          {t("Show more ({{count}})", {
            count: sortedEvents.length - visibleCount,
          })}
        </button>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Event Row
// ---------------------------------------------------------------------------

function CalendarEventRow({
  event,
  period,
}: {
  event: EventOccurrence
  period: CalendarListPeriod
}) {
  const prefix: React.ReactNode[] = []

  if (period.type === "month") {
    prefix.push(
      <span key="date" style={{ color: event.color, fontWeight: 500 }}>
        {dayjs(event.occurrenceDate).format("DD")}
      </span>,
    )
  } else if (period.type === "day") {
    if (event.allDay) {
      prefix.push(
        <span
          key="allday-bar"
          className="my-0.5 rounded-sm self-stretch"
          style={{
            borderLeft: `3px solid ${event.color}`,
          }}
        />,
      )
    } else {
      // Recurring or multi-day marker
      const isMultiOrRecurring = !!event.rrule || !!event.multiDay

      if (isMultiOrRecurring) {
        prefix.push(
          <span
            key="multi-bar"
            className="my-0.5 rounded-sm self-stretch"
            style={{
              borderLeft: `3px solid ${event.color}`,
            }}
          />,
        )
      }

      // Time display
      // Occurrences are real instants (floating ones already placed on the viewer's clock)
      const timeStr = dayjs.unix(event.occurrenceStart).format("HH:mm")

      prefix.push(
        <span
          key="time"
          style={{
            color: event.color,
            marginLeft: prefix.length > 0 ? "4px" : "0",
          }}
        >
          {timeStr}
        </span>,
      )
    }
  }

  const occurrenceText =
    event.occurrences && event.occurrences > 1 ? ` (x${event.occurrences})` : ""

  return (
    <div className="flex items-center text-[13px] text-muted-foreground">
      {prefix}
      <span className="ml-1 truncate min-w-0">
        {event.summary}
        {occurrenceText}
      </span>
    </div>
  )
}
